import { createContext, useContext, useSyncExternalStore } from "react";

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

interface ChatPanelSelectionState {
  selectedPreOrderId: string | null;
  highlightedPreOrderId: string | null;
  recentlyCreatedPreOrderId: string | null;
}

export function createChatPanelSelectionStore() {
  let state: ChatPanelSelectionState = {
    selectedPreOrderId: null,
    highlightedPreOrderId: null,
    recentlyCreatedPreOrderId: null,
  };
  const listeners = new Set<() => void>();

  const emit = () => {
    for (const l of listeners) l();
  };

  return {
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => state,

    setSelectedPreOrderId: (id: string | null) => {
      if (state.selectedPreOrderId === id) return;
      state = { ...state, selectedPreOrderId: id };
      emit();
    },
    setHighlightedPreOrderId: (id: string | null) => {
      if (state.highlightedPreOrderId === id) return;
      state = { ...state, highlightedPreOrderId: id };
      emit();
    },
    setRecentlyCreatedPreOrderId: (id: string | null) => {
      if (state.recentlyCreatedPreOrderId === id) return;
      state = { ...state, recentlyCreatedPreOrderId: id };
      emit();
    },
  };
}

export type ChatPanelSelectionStore = ReturnType<
  typeof createChatPanelSelectionStore
>;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const ChatPanelSelectionStoreContext =
  createContext<ChatPanelSelectionStore | null>(null);

// Shared no-op fallback for consumers rendered outside the provider
// (e.g. proposal cards on /ai-proposals or in the AI chat panel).
const FALLBACK_STORE = createChatPanelSelectionStore();

function useStore(): ChatPanelSelectionStore {
  return useContext(ChatPanelSelectionStoreContext) ?? FALLBACK_STORE;
}

// ---------------------------------------------------------------------------
// Selector hooks — only re-render when the derived value changes
// ---------------------------------------------------------------------------

/** Returns true only for the currently selected pre-order. */
export function useIsSelectedPreOrder(preOrderId: string): boolean {
  const store = useStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot().selectedPreOrderId === preOrderId,
  );
}

/** Returns true only for the currently highlighted pre-order. */
export function useIsHighlightedPreOrder(preOrderId: string): boolean {
  const store = useStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot().highlightedPreOrderId === preOrderId,
  );
}

/** Returns true only for the most recently created pre-order (auto-clears via timeout). */
export function useIsRecentlyCreatedPreOrder(preOrderId: string): boolean {
  const store = useStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot().recentlyCreatedPreOrderId === preOrderId,
  );
}

/** Returns the raw selectedPreOrderId — use sparingly, causes re-render on every change. */
export function useSelectedPreOrderId(): string | null {
  const store = useStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot().selectedPreOrderId,
  );
}

/** Returns the raw highlightedPreOrderId — use sparingly, causes re-render on every change. */
export function useHighlightedPreOrderId(): string | null {
  const store = useStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot().highlightedPreOrderId,
  );
}

/**
 * Returns the raw recentlyCreatedPreOrderId — use sparingly. Auto-clears on a
 * ~2.5 s timer (see useMarkRecentlyCreatedPreOrder), so subscribers re-render
 * twice per create: once when set, once when cleared.
 */
export function useRecentlyCreatedPreOrderId(): string | null {
  const store = useStore();
  return useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot().recentlyCreatedPreOrderId,
  );
}

/** Returns the store instance for imperative access (setters, snapshot reads in effects). */
export function useChatPanelSelectionStore(): ChatPanelSelectionStore {
  return useStore();
}
